import cn from 'classnames';
import { FC, HTMLProps, useMemo } from 'react';
import { Chip } from '../../components/chip/chip';
import { Input } from '../../components/input/input';
import { isMobile } from '../../helpers/devices';
import { SizeType } from '../../types/props';
import { Tooltip } from '../tooltip/tooltip';
import s from './multi-select.module.css';
import { NestedMultiSelectOptionsItem } from './nested-multi-select-options-item/nested-multi-select-options-item';
import { NestedIdValue, useMultiSelect } from './use-multi-select';

export interface MultiSelectProps
  extends Omit<HTMLProps<HTMLDivElement>, 'size' | 'value' | 'onSelect' | 'label' | 'required'> {
  size?: SizeType;
  options?: NestedIdValue[];
  value?: NestedIdValue[];
  onSelect: (value: NestedIdValue[]) => void;
  name?: string;
  'data-testid'?: string;
}

export const MultiSelect: FC<MultiSelectProps> = (props) => {
  const {
    size = 'lg',
    onSelect,
    options = [],
    value = [],
    className,
    name,
    disabled,
    placeholder,
  } = props;

  const { search, setSearch, withSearchOptions, t } = useMultiSelect(options);

  const sortedOptions = useMemo(() => {
    return withSearchOptions.sort((a, b) => {
      const aInValue = value.some((item) => item.id === a.id);
      const bInValue = value.some((item) => item.id === b.id);
      if (aInValue && !bInValue) return -1;
      if (!aInValue && bInValue) return 1;
      return 0;
    });
  }, [withSearchOptions, value]);

  const handleSelect = (el: NestedIdValue) => {
    const isParent = !!el.children?.length;

    if (isParent) {
      const children = el.children?.map((v) => v.id) || [];
      const isSomeUnchecked = children.some((v) => !value.map((v) => v.id).includes(v));
      const isAllUnchecked = value.every((v) => !children.includes(v.id));
      const removedChildren = value.filter((v) => !children.includes(v.id));

      if (isSomeUnchecked || isAllUnchecked) {
        const uniqueValues = [...removedChildren, ...(el.children || [])];
        onSelect(uniqueValues);
      } else {
        onSelect(removedChildren);
      }
    } else {
      const found = value.some((item) => item.id === el.id);
      if (found) {
        onSelect(value.filter((item) => item.id !== el.id));
      } else {
        onSelect([...value, el]);
      }
    }
  };

  return (
    <Tooltip
      withArrow
      disabled={disabled}
      wrapperClassName={cn(s.component, className, s[size])}
      triggerWrapperClassName={cn(s.selector, s[size])}
      name={name}
      data-testid={props['data-testid']}
      data-name={name}
      data-value={value.map((v) => v.value).join(', ')}
      trigger={
        <div className={s.selected}>
          {value.length >= 1 && (
            <Chip
              data-testid={`multi-select-chip-${value[0].id}`}
              className={s.item}
              onDelete={(e) => {
                handleSelect(value[0]);
                e.stopPropagation();
              }}
            >
              {value[0].value}
            </Chip>
          )}
          {value.length > 1 && (
            <div className={s.more}>
              {t('ещё')} {value.length - 1}
            </div>
          )}
          {!value.length && <div className={s.placeholder}>{placeholder}</div>}
        </div>
      }
      optionsWrapperClassName={cn(s.options)}
    >
      <Input
        placeholder={t('Поиск')}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        autoFocus={!isMobile()}
        name={name}
      />
      {sortedOptions.length ? (
        sortedOptions.map((child) => (
          <NestedMultiSelectOptionsItem
            item={child}
            onSelect={handleSelect}
            value={value}
            key={child.id}
          />
        ))
      ) : (
        <li key="empty" className={s.empty}>
          {t('Здесь пока пусто')}
        </li>
      )}
    </Tooltip>
  );
};
